import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useParams, useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/dashboard-api';
import ProductCell from './common/ProductCell';
import SizeChips from './common/SizeChips';

const PAGE_SIZE = 50;

// Mobile card layout for a single match
const MatchCard = ({ match }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Your Product</Typography>
        <ProductCell
          name={match.own_product_name}
          brand={match.own_product_brand}
          color={match.own_product_color}
          image={match.own_product_image}
        />
        <Box sx={{ mt: 1 }}>
          <SizeChips sizes={match.own_sizes} label="Your sizes" />
        </Box>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Partner Product</Typography>
        <ProductCell
          name={match.partner_product_name}
          brand={match.partner_product_brand}
          color={match.partner_product_color}
          image={match.partner_product_image}
        />
        <Box sx={{ mt: 1 }}>
          <SizeChips sizes={match.partner_sizes} label="Partner sizes" />
        </Box>
      </Box>
    </Box>
  </Paper>
);

// Desktop table skeleton
const TableSkeleton = () => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Your Product</TableCell>
          <TableCell>Your Sizes</TableCell>
          <TableCell>Partner Product</TableCell>
          <TableCell>Partner Sizes</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(6)].map((_, i) => (
          <TableRow key={i}>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="rectangular" width={48} height={64} sx={{ borderRadius: 1, flexShrink: 0 }} />
                <Box>
                  <Skeleton width={140} />
                  <Skeleton width={80} />
                </Box>
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Skeleton variant="rounded" width={32} height={24} />
                <Skeleton variant="rounded" width={32} height={24} />
                <Skeleton variant="rounded" width={32} height={24} />
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="rectangular" width={48} height={64} sx={{ borderRadius: 1, flexShrink: 0 }} />
                <Box>
                  <Skeleton width={140} />
                  <Skeleton width={80} />
                </Box>
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Skeleton variant="rounded" width={32} height={24} />
                <Skeleton variant="rounded" width={32} height={24} />
                <Skeleton variant="rounded" width={32} height={24} />
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// Mobile card skeleton
const CardSkeleton = () => (
  <Box>
    {[...Array(4)].map((_, i) => (
      <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton width={80} height={16} sx={{ mb: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
              <Skeleton variant="rectangular" width={48} height={64} sx={{ borderRadius: 1 }} />
              <Box>
                <Skeleton width={100} />
                <Skeleton width={60} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Skeleton variant="rounded" width={30} height={22} />
              <Skeleton variant="rounded" width={30} height={22} />
              <Skeleton variant="rounded" width={30} height={22} />
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={90} height={16} sx={{ mb: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
              <Skeleton variant="rectangular" width={48} height={64} sx={{ borderRadius: 1 }} />
              <Box>
                <Skeleton width={100} />
                <Skeleton width={60} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Skeleton variant="rounded" width={30} height={22} />
              <Skeleton variant="rounded" width={30} height={22} />
              <Skeleton variant="rounded" width={30} height={22} />
            </Box>
          </Box>
        </Box>
      </Paper>
    ))}
  </Box>
);

const PartnerMatchesPage = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [partnerName, setPartnerName] = useState('');
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const debounceTimer = useRef(null);

  const fetchProducts = useCallback(async (searchTerm, offset = 0, append = false) => {
    try {
      if (offset === 0) setLoading(true);
      else setLoadingMore(true);

      const response = await dashboardAPI.getPartnerProducts(partnerId, {
        search: searchTerm || undefined,
        limit: PAGE_SIZE,
        offset,
      });

      if (response.success) {
        const data = response.data;
        setPartnerName(data.partner.company_name);
        setProducts((prev) => append ? [...prev, ...data.matched_products] : data.matched_products);
        setTotalCount(data.total_count);
        setHasMore(data.has_more);
      } else {
        setError('Failed to load product matches');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [partnerId]);

  useEffect(() => {
    fetchProducts('');
  }, [fetchProducts]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchProducts(value, 0, false);
    }, 300);
  };

  const handleLoadMore = () => {
    fetchProducts(search, products.length, true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate('/network')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 600 }}>
          {partnerName || 'Partner'}
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3, ml: 5 }}>
        Identical product matches with this partner.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <TextField
        placeholder="Search by product name, brand, or color..."
        value={search}
        onChange={handleSearchChange}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        isMobile ? <CardSkeleton /> : <TableSkeleton />
      ) : products.length === 0 ? (
        <Typography color="textSecondary">
          {search ? 'No products match your search.' : 'No product matches found.'}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {totalCount.toLocaleString()} matched product{totalCount !== 1 ? 's' : ''}
          </Typography>

          {isMobile ? (
            // Mobile: card layout
            <Box>
              {products.map((match, idx) => (
                <MatchCard
                  key={`${match.own_product_id}-${match.partner_product_id}-${idx}`}
                  match={match}
                />
              ))}
            </Box>
          ) : (
            // Desktop: table layout
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Your Product</TableCell>
                    <TableCell>Your Sizes</TableCell>
                    <TableCell>Partner Product</TableCell>
                    <TableCell>Partner Sizes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((match, idx) => (
                    <TableRow key={`${match.own_product_id}-${match.partner_product_id}-${idx}`} hover>
                      <TableCell>
                        <ProductCell
                          name={match.own_product_name}
                          brand={match.own_product_brand}
                          color={match.own_product_color}
                          image={match.own_product_image}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '15%', verticalAlign: 'top', pt: 2 }}>
                        <SizeChips sizes={match.own_sizes} />
                      </TableCell>
                      <TableCell>
                        <ProductCell
                          name={match.partner_product_name}
                          brand={match.partner_product_brand}
                          color={match.partner_product_color}
                          image={match.partner_product_image}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '15%', verticalAlign: 'top', pt: 2 }}>
                        <SizeChips sizes={match.partner_sizes} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PartnerMatchesPage;
